import { Button, IconButton, Tag } from '@vrplatform/components';
import { createUUID } from '@vrplatform/components-legacy';
import { useApiQuery } from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import { day, hasValue } from '@vrplatform/utils';
import { FileButton, Group, Tooltip } from '@mantine/core';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import {
  isExpenseAttachmentDraft,
  useExpenseForm,
  type ExpenseFormInputs,
} from '../useExpenseForm';
import {
  ExpenseAttachmentModal,
  type ExpenseAttachmentFormValues,
} from './ExpenseAttachmentModal';
import { ACCEPT_EXPENSE_ATTACHMENT_TYPES } from './ExpenseAttachmentsInput.utils';
import { formatOwnerName } from '@vrplatform/ui-common';

type Attachment = ExpenseFormInputs['attachments'][number];

const getAttachmentKey = (attachment: Attachment) =>
  isExpenseAttachmentDraft(attachment)
    ? `draft-${attachment.tempId}`
    : `id-${attachment.id ?? attachment.filename}`;

const useExpenseAttachmentOwners = ({
  listingIds,
  reservationIds,
}: {
  listingIds: string[];
  reservationIds: string[];
}) => {
  const methods = useExpenseForm();

  const date = useWatch({
    control: methods.control,
    name: 'date',
  });

  return useApiQuery(
    async (api, signal, args) => {
      if (!args.date) return [];

      const uniqueReservationIds = Array.from(new Set(args.reservationIds));

      const date = args.date;

      const reservations = uniqueReservationIds.length
        ? await api
            .GET('/reservations', {
              params: {
                query: {
                  ids: args.reservationIds.join(','),
                },
              },
              signal,
            })
            .then((res) => {
              if (res.error) throw res.error;
              return res.data.data;
            })
        : [];

      const listingIds = [
        ...new Set([
          ...reservations
            .map((reservation) => reservation.listing?.id)
            .filter(hasValue),
          ...args.listingIds,
        ]),
      ];

      if (!listingIds.length) return [];

      const listingOwnerships = listingIds.length
        ? (
            await Promise.all(
              listingIds.map((listingId) =>
                api
                  .GET('/listings/ownership-periods', {
                    params: {
                      query: {
                        listingId,
                      },
                    },
                  })
                  .then((res) => {
                    if (res.error) throw res.error;
                    return res.data.data;
                  })
              )
            )
          ).flat()
        : [];

      const ownershipsThatIncludeDate = listingOwnerships.filter(
        (ownership) => {
          return (
            (day(ownership.startAt).isSame(date) ||
              day(ownership.startAt).isBefore(date)) &&
            (!ownership.endAt || day(ownership.endAt).isAfter(date))
          );
        }
      );

      const contactIds = Array.from(
        new Set(
          ownershipsThatIncludeDate.flatMap((ownership) =>
            ownership.members.map((member) => member.contactId).filter(hasValue)
          )
        )
      );

      return contactIds.length
        ? await Promise.all(
            contactIds.map((contactId) =>
              api
                .GET('/contacts/{id}', {
                  params: {
                    path: {
                      id: contactId,
                    },
                  },
                })
                .then((res) => {
                  if (res.error) throw res.error;
                  return formatOwnerName(res.data, {
                    lastNameFirst: false,
                    showEmpty: false,
                  });
                })
            )
          )
        : [];
    },
    {
      variables: {
        listingIds,
        reservationIds,
        date,
      },
      queryGroup: 'expenses',
      uniqueQueryKey: 'expense-attachments-listings',
    }
  );
};

export const ExpenseAttachmentsInput = () => {
  const methods = useExpenseForm();
  const [file, setFile] = useState<File | null>(null);
  const [editingAttachment, setEditingAttachment] = useState<Attachment | null>(
    null
  );
  const resetRef = useRef<() => void>(null);

  const attachments = useWatch({
    control: methods.control,
    name: 'attachments',
  });

  const pattern = useWatch({
    control: methods.control,
    name: 'pattern',
  });

  const lines = useWatch({
    control: methods.control,
    name: 'lines',
  });

  const listingIds = useMemo(
    () =>
      Array.from(
        new Set(lines?.map((line) => line.listingId).filter(hasValue) || [])
      ),
    [lines]
  );

  const reservationIds = useMemo(
    () =>
      Array.from(
        new Set(lines?.map((line) => line.reservationId).filter(hasValue) || [])
      ),
    [lines]
  );

  const ownersQuery = useExpenseAttachmentOwners({
    listingIds,
    reservationIds,
  });

  const closeModal = useCallback(() => {
    setFile(null);
    setEditingAttachment(null);
    resetRef.current?.();
  }, []);

  const removeAttachment = useCallback(
    (attachment: Attachment) => {
      const key = getAttachmentKey(attachment);
      const nextAttachments = (attachments ?? []).filter(
        (item) => getAttachmentKey(item) !== key
      );

      methods.setValue('attachments', nextAttachments, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [attachments, methods]
  );

  const updateAttachment = useCallback(
    (attachment: Attachment, values: ExpenseAttachmentFormValues) => {
      const nextAttachments = (attachments ?? []).map((item) =>
        getAttachmentKey(item) === getAttachmentKey(attachment)
          ? isExpenseAttachmentDraft(item)
            ? {
                ...item,
                filename: values.filename.trim(),
                isOwnerAccessible: values.isOwnerAccessible,
              }
            : ({
                ...item,
                filename: values.filename.trim(),
                isOwnerAccessible: values.isOwnerAccessible,
              } as Attachment)
          : item
      );

      methods.setValue('attachments', nextAttachments, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [attachments, methods]
  );

  const addAttachment = useCallback(
    async (values: ExpenseAttachmentFormValues) => {
      if (editingAttachment) {
        updateAttachment(editingAttachment, values);
        setEditingAttachment(null);
        return;
      }

      if (!file) {
        showWarnNotification({
          message: 'No file selected.',
        });
        return;
      }

      const filename = values.filename.trim();
      if (!filename) {
        showWarnNotification({
          message: 'File name is required.',
        });
        return;
      }

      const nextAttachment: Attachment = {
        tempId: createUUID(),
        file,
        filename,
        isOwnerAccessible: values.isOwnerAccessible,
      };

      const nextAttachments = [...(attachments ?? []), nextAttachment];

      methods.setValue('attachments', nextAttachments, {
        shouldDirty: true,
        shouldTouch: true,
      });

      setFile(null);
      resetRef.current?.();
    },
    [attachments, editingAttachment, file, methods, updateAttachment]
  );

  const disabled = methods.formState.disabled || !!pattern;

  const editingValues = editingAttachment
    ? {
        filename:
          'filename' in editingAttachment
            ? (editingAttachment.filename ?? '')
            : '',
        isOwnerAccessible:
          'isOwnerAccessible' in editingAttachment
            ? Boolean(editingAttachment.isOwnerAccessible)
            : false,
      }
    : undefined;

  return (
    <>
      <Group gap="xs" wrap="wrap" mt={3}>
        {(attachments ?? []).map((attachment) => {
          const key = getAttachmentKey(attachment);
          const isOwnerAccessible =
            'isOwnerAccessible' in attachment
              ? Boolean(attachment.isOwnerAccessible)
              : false;
          const visibilityIcon = isOwnerAccessible ? 'EyeIcon' : 'EyeSlashIcon';
          const tooltipLabel = isOwnerAccessible
            ? 'Owner can access'
            : 'Owner does not have access';

          return (
            <Tooltip key={key} label={tooltipLabel} withArrow>
              <Tag
                onClick={() => {
                  if (disabled) return;
                  setEditingAttachment(attachment);
                  setFile(null);
                }}
                leftIcon={visibilityIcon}
                leftIconSize={12}
                leftIconColor={(theme) =>
                  isOwnerAccessible
                    ? theme.colors[theme.primaryColor][6]
                    : theme.colors.neutral[4]
                }
                backgroundColor="#F5F5F5"
                borderColor="rgba(0,0,0,0.1)"
                radius={6}
                fontSize={12}
                color="#525252"
                height={'1.8rem'}
                rightSection={
                  <IconButton
                    icon="CrossIcon"
                    size="xs"
                    iconSize={14}
                    tooltip="Remove"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeAttachment(attachment);
                    }}
                    disabled={disabled}
                  />
                }
              >
                {attachment.filename}
              </Tag>
            </Tooltip>
          );
        })}

        <FileButton
          onChange={(nextFile) => {
            setEditingAttachment(null);
            setFile(nextFile);
          }}
          accept={ACCEPT_EXPENSE_ATTACHMENT_TYPES.join(',')}
          resetRef={resetRef}
          inputProps={
            {
              'data-testid': 'expense-attachments-input',
            } as any
          }
        >
          {(props) => (
            <Button
              {...props}
              size="xs"
              leftIcon="PlusIcon"
              disabled={disabled}
              sx={{
                height: '1.8rem',
              }}
            >
              Add
            </Button>
          )}
        </FileButton>
      </Group>

      <ExpenseAttachmentModal
        opened={!!file || !!editingAttachment}
        file={file}
        initialValues={editingValues}
        title={editingAttachment ? 'Edit file' : 'Add file'}
        owners={ownersQuery.data ?? []}
        onClose={closeModal}
        onSubmit={addAttachment}
      />
    </>
  );
};
